/* eslint-disable no-unneeded-ternary */
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation, useQueryClient } from 'react-query';
import { coreService } from 'network-utils';
import { FullPageLoader } from 'learning-panel-components';
import { translate } from 'translations';
import { selectToken, selectUserUuid, selectCustomerDisplayName } from '@redux/slices/userSlice';
import { showAlert } from '@redux/slices/informationSlice';
import { getUserLearningPath, useGetUserLearningPath } from '../../queries/core/learningPath';
import { useGetAccountData } from '../../queries/platform/accountSettings';
import LearningPath from './learningPath';
import { LearningPathData, LearningPathNodeProps, LearningPathNodeType } from './learningPathInterfaces';
import { contentTypeToNodeType } from './utils';

const LearningPathContainer: FC = () => {
    const learningPathUuid = new URLSearchParams(window.location.search).get('uuid');
    const userUuid = useSelector(selectUserUuid);
    const token = useSelector(selectToken);
    const customerDisplayName = useSelector(selectCustomerDisplayName);

    const queryClient = useQueryClient();
    const dispatch = useDispatch();

    const [learningPathInput, setLearningPathInput] = useState<LearningPathData | null>(null);
    const [nickname, setNickname] = useState<string>('');
    const [profilePicture, setProfilePicture] = useState<string>('');
    const [startCalled, setStartCalled] = useState<boolean>(false);
    const [isSelectingNode, setIsSelectingNode] = useState<boolean>(false);

    const { data: userLearningPath } = useGetUserLearningPath(learningPathUuid, token);
    const { data: accountData } = useGetAccountData(userUuid);

    const generalErrorText = translate('generalError');

    const showError = () => {
        dispatch(
            showAlert({
                value: generalErrorText,
                status: 'error'
            })
        );
    };

    const performStartPath = async (): Promise<{ success: boolean; error?: string }> => {
        const startPathResponse = await coreService.learningPathApi.start(token, learningPathUuid as string);
        return startPathResponse;
    };

    const { mutate: mutateStartPath } = useMutation(performStartPath, {
        onSuccess: response => {
            if (!response?.success) {
                showError();
                return;
            }

            queryClient.setQueryData([getUserLearningPath, learningPathUuid, token], (oldData: any) => ({
                ...oldData,
                status: coreService.ProgressStatus.NOT_STARTED,
                nodes: oldData.nodes.map((node: any) => {
                    if (node.parents.length === 0) {
                        return { ...node, status: 'not_started' };
                    }
                    return node;
                })
            }));

            queryClient.invalidateQueries([getUserLearningPath, learningPathUuid, token]);
        },
        onError: () => {
            showError();
        }
    });

    useEffect(() => {
        if (!userLearningPath || userLearningPath.success === false) return;

        if (userLearningPath.status === null && !startCalled) {
            setStartCalled(true);
            mutateStartPath();
        }

        setLearningPathInput({
            uuid: userLearningPath.uuid,
            name: userLearningPath.name,
            description: userLearningPath.description,
            level: userLearningPath.level,
            durationMinutes: userLearningPath.duration,
            potentialScore: userLearningPath.potentialScore,
            status: userLearningPath.status,
            completedAt: userLearningPath.completedAt,
            lastSelectedNodeId: userLearningPath.lastSelectedNodeId,
            score: userLearningPath.score,
            languages: userLearningPath.languages,
            progress: userLearningPath.percent,
            badge: userLearningPath.badge || undefined,
            nodes: userLearningPath.nodes.map(node => {
                const itemTypesCount = {
                    [coreService.LearningPathContentType.LESSON]: 0,
                    [coreService.LearningPathContentType.CHALLENGE]: 0,
                    [coreService.LearningPathContentType.COURSE]: 0,
                    [coreService.LearningPathContentType.SYSTEM_ASSESSMENT]: 0
                };

                const typeSet = new Set<coreService.LearningPathContentType>();
                node.items.forEach(item => {
                    itemTypesCount[item.type]++;
                    typeSet.add(item.type);
                });

                let type: LearningPathNodeType;
                if (typeSet.size === 1 && node.items.length === 1) {
                    type = contentTypeToNodeType(node.items[0].type);
                } else {
                    type = LearningPathNodeType.MIXED;
                }

                const firstUncompleted = node.items.find(item => item.status !== coreService.ProgressStatus.COMPLETED);

                return {
                    id: node.id,
                    uuid: node.uuid,
                    name: node.name,
                    durationMinutes: node.durationMinutes,
                    potentialScore: node.potentialScore,
                    score: node.score,
                    badge: node.badge,
                    status: node.status,
                    children: node.children,
                    progressPercentage: node.progressPercentage,
                    parents: node.parents,
                    nextUrl: firstUncompleted?.url || '',
                    type,
                    itemTypesCount,
                    numberOfItems: node.items.length,
                    isMandatory: node.isMandatory === false ? false : true
                };
            })
        });
    }, [mutateStartPath, startCalled, userLearningPath]);

    useEffect(() => {
        if (!accountData || !accountData.account_data) return;

        setNickname(accountData.account_data.nickname);
        setProfilePicture(accountData.account_data.profile_picture_url);
    }, [accountData]);

    const performSelectNode = async (input: {
        node: LearningPathNodeProps;
    }): Promise<{ success: boolean; error?: string }> => {
        const selectNodeResponse = await coreService.learningPathApi.selectNode(token, input.node.uuid);
        return selectNodeResponse;
    };

    const { mutate: mutateSelectNode } = useMutation(performSelectNode, {
        onSuccess: (response, variables) => {
            setIsSelectingNode(false);
            if (!response?.success) {
                showError();
                return;
            }

            const { node } = variables;

            if (node.nextUrl !== '') {
                window.location.href = node.nextUrl;
                return;
            }

            // node content was completed in advance, simply mark it as completed
            queryClient.setQueryData([getUserLearningPath, learningPathUuid, token], (oldData: any) => ({
                ...oldData,
                nodes: oldData.nodes.map((oldNode: any) => {
                    if (oldNode.uuid === node.uuid) {
                        return { ...oldNode, status: coreService.ProgressStatus.COMPLETED };
                    }
                    return oldNode;
                })
            }));

            queryClient.invalidateQueries([getUserLearningPath, learningPathUuid, token]);
        },
        onError: () => {
            setIsSelectingNode(false);
            showError();
        }
    });

    const onSelectNode = (node: LearningPathNodeProps) => {
        if (isSelectingNode) return;

        setIsSelectingNode(true);
        mutateSelectNode({ node });
    };

    return learningPathInput ? (
        <LearningPath
            tenant={customerDisplayName}
            {...learningPathInput}
            nickname={nickname}
            profilePicture={profilePicture}
            onSelectNode={onSelectNode}
        />
    ) : (
        <FullPageLoader />
    );
};

export default LearningPathContainer;
